import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../api/client";
import ScoreBadge from "../components/ScoreBadge";
import FindingCard from "../components/FindingCard";
import IssueCard from "../components/IssueCard";
import ImageGrid from "../components/ImageGrid";
import ImageModal from "../components/ImageModal";
import LoadingSpinner from "../components/LoadingSpinner";

function StoreDetail() {
    const { siteId } = useParams();
    const [store, setStore] = useState(null);
    const [zones, setZones] = useState([]);
    const [tab, setTab] = useState("reports");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Findings state
    const [findings, setFindings] = useState([]);
    const [findingsTotal, setFindingsTotal] = useState(0);
    const [findingsPage, setFindingsPage] = useState(1);
    const [findingsSeverity, setFindingsSeverity] = useState("");
    const [findingsCategory, setFindingsCategory] = useState("");
    const [findingsLoading, setFindingsLoading] = useState(false);

    // Issues state
    const [issues, setIssues] = useState([]);
    const [issuesLoading, setIssuesLoading] = useState(false);

    // Images state
    const [images, setImages] = useState([]);
    const [imagesDate, setImagesDate] = useState(new Date().toISOString().split("T")[0]);
    const [imagesZone, setImagesZone] = useState("");
    const [imagesLoading, setImagesLoading] = useState(false);

    // Reports state
    const [reportsData, setReportsData] = useState(null);
    const [reportsLoading, setReportsLoading] = useState(false);

    // Infrastructure state
    const [infraDetail, setInfraDetail] = useState(null);
    const [infraLoading, setInfraLoading] = useState(false);

    // Modal state
    const [modalImages, setModalImages] = useState(null);
    const [modalIndex, setModalIndex] = useState(0);

    // Scan Now state
    const [scanTriggering, setScanTriggering] = useState(false);
    const [scanMsg, setScanMsg] = useState(null);

    // Load store details
    useEffect(() => {
        setLoading(true);
        api.store(siteId)
            .then((res) => {
                setStore(res.store);
                setZones(res.zoneBreakdown || []);
                setError(null);
            })
            .catch((err) => setError(err.message))
            .finally(() => setLoading(false));
    }, [siteId]);

    // Load findings
    const loadFindings = useCallback(async (page = 1, append = false) => {
        setFindingsLoading(true);
        try {
            const params = { page, pageSize: 50 };
            if (findingsSeverity) params.severity = findingsSeverity;
            if (findingsCategory) params.category = findingsCategory;
            const res = await api.findings(siteId, params);
            if (append) {
                setFindings((prev) => [...prev, ...(res.findings || [])]);
            } else {
                setFindings(res.findings || []);
            }
            setFindingsTotal(res.total || 0);
            setFindingsPage(page);
        } catch (err) {
            console.error("Findings error:", err);
        } finally {
            setFindingsLoading(false);
        }
    }, [siteId, findingsSeverity, findingsCategory]);

    // Load issues
    const loadIssues = useCallback(async () => {
        setIssuesLoading(true);
        try {
            const res = await api.issues(siteId);
            setIssues(res.issues || []);
        } catch (err) {
            console.error("Issues error:", err);
        } finally {
            setIssuesLoading(false);
        }
    }, [siteId]);

    // Load images
    const loadImages = useCallback(async () => {
        setImagesLoading(true);
        try {
            const params = { date: imagesDate };
            if (imagesZone) params.zone = imagesZone;
            const res = await api.images(siteId, params);
            setImages(res.images || []);
        } catch (err) {
            console.error("Images error:", err);
        } finally {
            setImagesLoading(false);
        }
    }, [siteId, imagesDate, imagesZone]);

    // Load infrastructure
    const loadInfra = useCallback(async () => {
        setInfraLoading(true);
        try {
            const res = await api.camerasForStore(siteId);
            setInfraDetail(res);
        } catch (err) {
            console.error("Infra error:", err);
        } finally {
            setInfraLoading(false);
        }
    }, [siteId]);

    // Load reports
    const loadReports = useCallback(async () => {
        setReportsLoading(true);
        try {
            const res = await api.storeReports(siteId);
            setReportsData(res);
        } catch (err) {
            console.error("Reports error:", err);
        } finally {
            setReportsLoading(false);
        }
    }, [siteId]);

    // Load tab data when tab changes
    useEffect(() => {
        if (tab === "reports") loadReports();
        if (tab === "findings") loadFindings(1);
        if (tab === "issues") loadIssues();
        if (tab === "images") loadImages();
        if (tab === "infra") loadInfra();
    }, [tab, loadReports, loadFindings, loadIssues, loadImages, loadInfra]);

    // Get unique zones from the zone breakdown for the filter
    const zoneOptions = [...new Set(zones.map((z) => z.zone))];
    // Get unique categories
    const categoryOptions = [...new Set(zones.map((z) => z.category))];

    const handleScanNow = async () => {
        setScanTriggering(true);
        setScanMsg(null);
        try {
            const result = await api.triggerScan(siteId);
            setScanMsg({ type: "success", text: result.message || "Scan queued successfully" });
        } catch (err) {
            setScanMsg({ type: "error", text: err.message || "Failed to trigger scan" });
        } finally {
            setScanTriggering(false);
            setTimeout(() => setScanMsg(null), 3000);
        }
    };

    if (loading) return <LoadingSpinner message="Loading store..." />;

    if (error) {
        return (
            <div className="empty-state">
                <h3>Failed to load store</h3>
                <p>{error}</p>
                <Link to="/" style={{ color: "var(--accent)" }}>Back to Dashboard</Link>
            </div>
        );
    }

    const scansToday = store?.totalAssessments || 0;

    return (
        <div>
            {/* Header */}
            <div className="page-header">
                <p style={{ marginBottom: 4 }}>
                    <Link to="/" style={{ color: "var(--accent)", textDecoration: "none", fontSize: 13 }}>
                        ← Dashboard
                    </Link>
                </p>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <h2 style={{ margin: 0 }}>{store?.name || `Store ${siteId}`}</h2>
                    <button
                        onClick={handleScanNow}
                        disabled={scanTriggering}
                        style={{
                            padding: "6px 16px", fontSize: 13, fontWeight: 600, cursor: scanTriggering ? "not-allowed" : "pointer",
                            border: "1px solid var(--primary)", borderRadius: 6,
                            background: "var(--primary)", color: "#fff",
                            opacity: scanTriggering ? 0.6 : 1,
                        }}
                    >
                        {scanTriggering ? "Triggering..." : "Scan Now"}
                    </button>
                </div>
                <p>
                    Site #{store?.siteId || siteId}
                    {store?.state && ` · ${store.state}`}
                    {store?.lastScanAt && ` · Last scan ${new Date(store.lastScanAt).toLocaleString()}`}
                </p>
                {scanMsg && (
                    <div style={{
                        padding: "6px 12px", borderRadius: 6, fontSize: 13, marginTop: 4,
                        background: scanMsg.type === "success" ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.1)",
                        color: scanMsg.type === "success" ? "#22c55e" : "#ef4444",
                        border: `1px solid ${scanMsg.type === "success" ? "#22c55e" : "#ef4444"}`,
                    }}>
                        {scanMsg.text}
                    </div>
                )}
            </div>

            {/* Stats row */}
            <div className="stats-grid">
                <div className="stat-card">
                    <div className="stat-label">Overall Score</div>
                    <div className="stat-value">
                        {store?.latestScore != null ? Math.round(store.latestScore) : "\u2014"}
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-label">Open Issues</div>
                    <div className="stat-value">{store?.openIssues || 0}</div>
                </div>
                <div className="stat-card">
                    <div className="stat-label">Total Scans</div>
                    <div className="stat-value">{scansToday}</div>
                </div>
            </div>

            {/* Tabs */}
            <div className="tabs">
                <button
                    className={`tab ${tab === "reports" ? "active" : ""}`}
                    onClick={() => setTab("reports")}
                >
                    Reports
                </button>
                <button
                    className={`tab ${tab === "findings" ? "active" : ""}`}
                    onClick={() => setTab("findings")}
                >
                    AI Findings
                </button>
                <button
                    className={`tab ${tab === "issues" ? "active" : ""}`}
                    onClick={() => setTab("issues")}
                >
                    Issues
                </button>
                <button
                    className={`tab ${tab === "images" ? "active" : ""}`}
                    onClick={() => setTab("images")}
                >
                    Camera Captures
                </button>
                <button
                    className={`tab ${tab === "infra" ? "active" : ""}`}
                    onClick={() => setTab("infra")}
                >
                    Infrastructure
                </button>
            </div>

            {/* Tab content */}
            <div className="tab-content">
                {tab === "reports" && (
                    <div>
                        {reportsLoading ? (
                            <LoadingSpinner message="Loading reports..." />
                        ) : reportsData ? (
                            <div>
                                {/* Score trend */}
                                {reportsData.trend && reportsData.trend.length > 0 && (
                                    <div style={{ marginBottom: 24 }}>
                                        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 12 }}>
                                            Score Trend
                                        </div>
                                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 8 }}>
                                            {reportsData.trend.slice(0, 14).map((d) => (
                                                <div key={d.scan_date} style={{
                                                    padding: "10px 12px", background: "var(--bg-secondary)",
                                                    border: "1px solid var(--border)", borderRadius: 8
                                                }}>
                                                    <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 4 }}>
                                                        {new Date(d.scan_date + "T00:00:00").toLocaleDateString([], { month: "short", day: "numeric" })}
                                                    </div>
                                                    <div style={{
                                                        fontSize: 20, fontWeight: 700,
                                                        color: d.avg_score >= 8 ? "#22c55e" : d.avg_score >= 5 ? "#eab308" : "#ef4444"
                                                    }}>
                                                        {Math.round(d.avg_score)}/10
                                                    </div>
                                                    <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
                                                        {d.check_count} check{d.check_count !== 1 ? "s" : ""}
                                                        {d.min_score !== d.max_score && ` (${d.min_score}–${d.max_score})`}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Recent assessments */}
                                {reportsData.assessments && reportsData.assessments.length > 0 && (
                                    <div>
                                        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 12 }}>
                                            Recent Assessments
                                        </div>
                                        <table className="store-table">
                                            <thead>
                                                <tr>
                                                    <th>Date</th>
                                                    <th>Preset</th>
                                                    <th>Check</th>
                                                    <th>Score</th>
                                                    <th>Summary</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {reportsData.assessments.map((a) => (
                                                    <tr key={a.id}>
                                                        <td style={{ whiteSpace: "nowrap", fontSize: 12 }}>
                                                            {new Date(a.assessed_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                                                        </td>
                                                        <td style={{ fontWeight: 600, fontSize: 13 }}>{a.preset_name}</td>
                                                        <td style={{ fontSize: 12, color: "var(--text-secondary)" }}>{a.check_type}</td>
                                                        <td>
                                                            <ScoreBadge score={a.score} />
                                                        </td>
                                                        <td style={{ fontSize: 12, color: "var(--text-secondary)", maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                            {a.summary || "\u2014"}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                {(!reportsData.trend || reportsData.trend.length === 0) && (!reportsData.assessments || reportsData.assessments.length === 0) && (
                                    <div className="empty-state"><p>No report data available yet</p></div>
                                )}
                            </div>
                        ) : (
                            <div className="empty-state"><p>No report data available</p></div>
                        )}
                    </div>
                )}

                {tab === "findings" && (
                    <div>
                        <div className="filter-row">
                            <select
                                className="filter-select"
                                value={findingsCategory}
                                onChange={(e) => setFindingsCategory(e.target.value)}
                            >
                                <option value="">All Categories</option>
                                {categoryOptions.map((c) => (
                                    <option key={c} value={c}>{c}</option>
                                ))}
                            </select>
                            <select
                                className="filter-select"
                                value={findingsSeverity}
                                onChange={(e) => setFindingsSeverity(e.target.value)}
                            >
                                <option value="">All Severities</option>
                                <option value="critical">Critical</option>
                                <option value="warning">Warning</option>
                                <option value="high">High</option>
                                <option value="info">Info</option>
                                <option value="low">Low</option>
                            </select>
                        </div>

                        {findingsLoading && findings.length === 0 ? (
                            <LoadingSpinner message="Loading findings..." />
                        ) : findings.length > 0 ? (
                            <div className="findings-list">
                                {findings.map((f) => (
                                    <FindingCard
                                        key={f.id}
                                        finding={f}
                                        onImageClick={(url) => {
                                            setModalImages([{ url }]);
                                            setModalIndex(0);
                                        }}
                                    />
                                ))}
                                {findings.length < findingsTotal && (
                                    <button
                                        className="load-more-btn"
                                        onClick={() => loadFindings(findingsPage + 1, true)}
                                        disabled={findingsLoading}
                                    >
                                        {findingsLoading ? "Loading..." : "Load More"}
                                    </button>
                                )}
                            </div>
                        ) : (
                            <div className="empty-state"><p>No findings</p></div>
                        )}
                    </div>
                )}

                {tab === "issues" && (
                    <div>
                        {issuesLoading ? (
                            <LoadingSpinner message="Loading issues..." />
                        ) : issues.length > 0 ? (
                            <div className="issues-list">
                                {issues.map((issue) => (
                                    <IssueCard
                                        key={issue.id}
                                        issue={issue}
                                        onAction={loadIssues}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className="empty-state"><p>No open issues</p></div>
                        )}
                    </div>
                )}

                {tab === "images" && (
                    <div>
                        <div className="filter-row">
                            <input
                                type="date"
                                className="filter-select"
                                value={imagesDate}
                                onChange={(e) => setImagesDate(e.target.value)}
                            />
                            <select
                                className="filter-select"
                                value={imagesZone}
                                onChange={(e) => setImagesZone(e.target.value)}
                            >
                                <option value="">All Zones</option>
                                {zoneOptions.map((z) => (
                                    <option key={z} value={z}>{z}</option>
                                ))}
                            </select>
                        </div>

                        {imagesLoading ? (
                            <LoadingSpinner message="Loading images..." />
                        ) : (
                            <ImageGrid
                                images={images}
                                onImageClick={(idx) => {
                                    setModalImages(images);
                                    setModalIndex(idx);
                                }}
                            />
                        )}
                    </div>
                )}

                {tab === "infra" && (
                    <div>
                        {infraLoading ? (
                            <LoadingSpinner message="Loading infrastructure..." />
                        ) : infraDetail ? (
                            <div>
                                {/* NVR details */}
                                {infraDetail.nvr && (
                                    <div className="card" style={{ marginBottom: 16 }}>
                                        <div className="card-title">NVR Details</div>
                                        <div style={{ display: "flex", gap: 24, flexWrap: "wrap", fontSize: 13, color: "var(--text-secondary)", marginBottom: 12 }}>
                                            <span><strong>IP:</strong> {infraDetail.nvr.ip_address}</span>
                                            <span><strong>Status:</strong>{" "}
                                                <span style={{ color: infraDetail.nvr.status === "OK" ? "#22c55e" : "#ef4444", fontWeight: 600 }}>
                                                    {infraDetail.nvr.status}
                                                </span>
                                            </span>
                                            {infraDetail.nvr.model && <span><strong>Model:</strong> {infraDetail.nvr.model}</span>}
                                            {infraDetail.nvr.firmware && <span><strong>Firmware:</strong> {infraDetail.nvr.firmware}</span>}
                                            {infraDetail.nvr.serial && <span><strong>Serial:</strong> <span style={{ fontSize: 11 }}>{infraDetail.nvr.serial.substring(0, 30)}</span></span>}
                                            <span><strong>Cameras:</strong> {infraDetail.nvr.cameras_online}/{infraDetail.nvr.cameras_total} online</span>
                                            <span><strong>Retention:</strong> {infraDetail.nvr.retention_days}d</span>
                                            {infraDetail.nvr.work_mode && <span><strong>Mode:</strong> {infraDetail.nvr.work_mode}</span>}
                                        </div>
                                        {/* HDD storage bar */}
                                        {infraDetail.nvr.hdd_count > 0 && (
                                            <div style={{ marginBottom: 12 }}>
                                                <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 4 }}>
                                                    Storage: {infraDetail.nvr.hdd_count} HDD, {infraDetail.nvr.hdd_total_gb ? `${Number(infraDetail.nvr.hdd_total_gb).toLocaleString()} GB total` : ""} {"\u2014"} {infraDetail.nvr.hdd_status}
                                                </div>
                                                <div style={{ height: 8, background: "var(--bg-hover)", borderRadius: 4, overflow: "hidden", maxWidth: 400 }}>
                                                    <div style={{
                                                        height: "100%", borderRadius: 4,
                                                        width: `${Math.min(infraDetail.nvr.hdd_used_pct || 0, 100)}%`,
                                                        background: (infraDetail.nvr.hdd_used_pct || 0) > 90 ? "#ef4444" : (infraDetail.nvr.hdd_used_pct || 0) > 75 ? "#f97316" : "#22c55e",
                                                    }} />
                                                </div>
                                                <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>{infraDetail.nvr.hdd_used_pct || 0}% used</div>
                                            </div>
                                        )}
                                        {/* Timezone/DST issues */}
                                        {(infraDetail.nvr.needs_clock || infraDetail.nvr.needs_dst) && (
                                            <div style={{ fontSize: 12, color: "#f97316" }}>
                                                {infraDetail.nvr.needs_clock ? "Clock needs correction" : ""}{infraDetail.nvr.needs_clock && infraDetail.nvr.needs_dst ? " | " : ""}{infraDetail.nvr.needs_dst ? "DST needs update" : ""}
                                            </div>
                                        )}
                                        {infraDetail.nvr.issues && (
                                            <div style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 8 }}>
                                                <strong>Issues:</strong> {infraDetail.nvr.issues.replace(/;/g, ", ")}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Camera table */}
                                {infraDetail.cameras && infraDetail.cameras.length > 0 && (
                                    <div className="card" style={{ marginBottom: 16 }}>
                                        <div className="card-title">Cameras ({infraDetail.cameras.length})</div>
                                        <table className="store-table" style={{ fontSize: 12 }}>
                                            <thead>
                                                <tr><th>Cam</th><th>Quality</th><th>Size</th><th>Classification</th><th>Overlay</th></tr>
                                            </thead>
                                            <tbody>
                                                {infraDetail.cameras.map((c, i) => (
                                                    <tr key={i}>
                                                        <td style={{ fontWeight: 600 }}>{c.cam_num}</td>
                                                        <td style={{ color: c.quality === "good" ? "#22c55e" : c.quality === "poor" ? "#f97316" : c.quality === "black" ? "#ef4444" : "var(--text-muted)" }}>{c.quality || "\u2014"}</td>
                                                        <td>{c.size_kb ? `${c.size_kb}KB` : "\u2014"}</td>
                                                        <td>{c.classification || "\u2014"}</td>
                                                        <td style={{ color: "var(--text-muted)" }}>{c.overlay_name || "\u2014"}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                {/* Midday images */}
                                {infraDetail.middayImages && infraDetail.middayImages.length > 0 && (
                                    <div className="card">
                                        <div className="card-title">Midday Captures ({infraDetail.middayImages.length} cameras)</div>
                                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                                            {infraDetail.middayImages.map((img, i) => (
                                                <div key={i} onClick={() => { setModalImages(infraDetail.middayImages.map(m => ({ url: m.url, label: `Cam ${m.camera}` }))); setModalIndex(i); }}
                                                    style={{ cursor: "pointer", position: "relative", borderRadius: 6, overflow: "hidden", border: "1px solid var(--border)" }}>
                                                    <img src={img.url} alt={`Camera ${img.camera}`}
                                                        style={{ width: 180, height: 120, objectFit: "cover", display: "block" }} loading="lazy" />
                                                    <div style={{
                                                        position: "absolute", bottom: 0, left: 0, right: 0,
                                                        background: "rgba(0,0,0,0.7)", padding: "3px 6px",
                                                        fontSize: 10, color: "#fff", display: "flex", justifyContent: "space-between"
                                                    }}>
                                                        <span style={{ fontWeight: 600 }}>Cam {img.camera}</span>
                                                        <span style={{ opacity: 0.7 }}>{img.date}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* No data state */}
                                {!infraDetail.nvr && (!infraDetail.cameras || infraDetail.cameras.length === 0) && (
                                    <div className="empty-state"><p>No infrastructure data available for this store</p></div>
                                )}
                            </div>
                        ) : (
                            <div className="empty-state"><p>No infrastructure data available</p></div>
                        )}
                    </div>
                )}
            </div>

            {/* Image modal */}
            {modalImages && (
                <ImageModal
                    images={modalImages}
                    currentIndex={modalIndex}
                    onClose={() => setModalImages(null)}
                    onPrev={modalIndex > 0 ? () => setModalIndex(modalIndex - 1) : null}
                    onNext={modalIndex < modalImages.length - 1 ? () => setModalIndex(modalIndex + 1) : null}
                />
            )}
        </div>
    );
}

export default StoreDetail;
